import { Settings, UserPlus, UserSearch } from "lucide-react";
import { type FC, useState } from "react";
import { Link, type LinkProps } from "react-router-dom";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { ReactComponent as EmailIcon } from "./email.svg";
import { ReactComponent as EmailSearchIcon } from "./email-search.svg";
import { ReactComponent as HistoriesIcon } from "./histories.svg";
import { ReactComponent as ScheduleIcon } from "./schedule.svg";

interface SideBarMenuItemProps {
  id: string;
  title: string;
  isActive: boolean;
  to: LinkProps["to"];
  isSidebarOpen?: boolean;
}

const iconMap: Record<string, React.ReactNode> = {
  bulkEmail: <EmailIcon className="w-5 h-5" />,
  emailsHistories: <HistoriesIcon className="w-5 h-5" />,
  emailsSchedules: <ScheduleIcon className="w-5 h-5" />,
  emailsSearch: <EmailSearchIcon className="w-5 h-5" />,
  matching: <UserSearch className="w-5 h-5" />,
  invitations: <UserPlus className="w-5 h-5" />,
  admin: <Settings className="w-5 h-5" />,
};

const SideBarMenuItemUI: FC<SideBarMenuItemProps> = ({
  id,
  title,
  to,
  isActive,
  isSidebarOpen = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const icon = iconMap[id];

  const handleMouseEnter = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      top: rect.top + rect.height / 2,
      left: rect.right + 12,
    });
    setIsHovered(true);
  };

  return (
    <li>
      <div className="relative">
        {/* アクティブインジケーター（左側のバー） */}
        {isActive && (
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-white rounded-r-full" />
        )}
        <Button
          asChild
          variant="ghost"
          className={cn(
            "w-full text-white/90 hover:bg-white/10 hover:text-white",
            isSidebarOpen ? "justify-start" : "justify-center",
            "transition-all duration-200 ease-in-out",
            "h-9 px-3 rounded-lg relative",
            "hover:translate-x-0.5",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent",
            isActive && "bg-white/15 text-white font-medium shadow-sm",
          )}
        >
          <Link
            to={to}
            className={cn(
              "flex items-center relative",
              isSidebarOpen ? "justify-start px-3" : "justify-center",
            )}
            onMouseEnter={!isSidebarOpen ? handleMouseEnter : undefined}
            onMouseLeave={
              !isSidebarOpen ? () => setIsHovered(false) : undefined
            }
          >
            {icon && (
              <span
                className={cn(
                  "transition-transform duration-200",
                  isActive && "scale-105",
                  isSidebarOpen && "mr-3",
                )}
              >
                {icon}
              </span>
            )}
            {isSidebarOpen && (
              <span className="text-sm whitespace-nowrap">{title}</span>
            )}
          </Link>
        </Button>
        {/* ツールチップ（fixed position、サイドバーが閉じている時のみ表示） */}
        {!isSidebarOpen && isHovered && (
          <div
            className={cn(
              "text-xs whitespace-nowrap pointer-events-none",
              "opacity-100 scale-100",
              "transition-all duration-200",
              "px-2.5 py-1",
              "bg-gradient-to-r from-sidebar-left to-sidebar-right",
              "text-white rounded-lg shadow-lg",
              "border border-white/20",
              "z-50 origin-left",
              "fixed",
              "max-w-none",
            )}
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
              transform: "translateY(-50%)",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </div>
        )}
      </div>
    </li>
  );
};

export default SideBarMenuItemUI;
